import { BaseDoc, Constants, DateUtils, PersonDoc, SubmittalDoc, SubmittalRevision, WorkflowState, WorkflowStateComplete } from 'common';
import { PeopleService } from '../service/PeopleService';
import { WorkflowRenderState } from '..';

/**
 * Pure navigation and state-interrogation logic over workflow and revision structures.
 */
export class WorkflowUtils {
  /**
   * Computes the common render-state fields shared by RFI and Submittal detail views:
   * - stateComplete: the last completed workflow state entry
   * - returnToState: the state to return to (undefined = no return button, null = would return before first state)
   * - isResponsible: whether the current user is in the currentState's dueBy list
   */
  static getWorkflowRenderState(states: WorkflowState[], statusHistory: WorkflowStateComplete[], currentState: WorkflowState, userId: string): WorkflowRenderState {
    if (states?.length === 0) {
      return { stateComplete: undefined, returnToState: undefined, isResponsible: false };
    }
    const stateComplete = statusHistory[statusHistory.length - 1];
    const lastCompletedStateIndex = states.findIndex(s => s.id === stateComplete?.state_id);

    let returnToState: WorkflowState | null | undefined = undefined;
    if (lastCompletedStateIndex > 0) {
      returnToState = states[lastCompletedStateIndex - 1];
    } else if (lastCompletedStateIndex === 0) {
      returnToState = null;
    }
    const isResponsible = currentState?.dueBy?.includes(userId) ?? false;

    return { stateComplete, returnToState, isResponsible };
  }

  /**
   * Prorate workflow state durations so the total fits within the window between createDate and dueDate.
   */
  static getProratedWorkflow(states: WorkflowState[], createDate: Date, dueDate: Date): WorkflowState[] {
    const workflowDueDays = states.reduce((sum, step) => sum + (step.dueDays || 0), 0);
    const projectedDueTimestamp = createDate.getTime() + workflowDueDays * DateUtils.MILLIS_PER_DAY;
    if (!dueDate || dueDate.getTime() > projectedDueTimestamp) return states; // No due date, or ahead of states return original states

    const availableDays = (dueDate.getTime() - createDate.getTime()) / DateUtils.MILLIS_PER_DAY;
    // Prorate dueDays
    const prorated = [];
    let sum = 0;
    for (let i = 0; i < states.length; i++) {
      let proratedDays = Math.max(Math.floor((states[i].dueDays / workflowDueDays) * availableDays), 1);
      prorated.push({ ...states[i], dueDays: proratedDays });
      sum += proratedDays;
    }
    // Adjust the last step to make the total match availableDays
    if (sum !== availableDays && prorated.length > 0) {
      prorated[prorated.length - 1].dueDays += availableDays - sum;
    }
    return prorated;
  }

  static validateIfCanChangeWorkflowState(currentState: WorkflowState, currentUserId: string, people: PersonDoc[]): { canChange: boolean; responsibleNames?: string } {
    const isResponsible = currentState.dueBy?.includes(currentUserId);
    const canChangeState = currentState.dueBy && currentState.dueBy.length > 0 && isResponsible;
    if (!canChangeState) {
      return { canChange: false };
    }

    const responsibleNames = currentState.dueBy
      .map(personId => {
        const person = PeopleService.findPerson(people, personId);
        return person ? person.first_name + ' ' + person.last_name : null;
      })
      .join(', ');
    return { canChange: canChangeState, responsibleNames: responsibleNames };
  }

  static validateIfHasAcceptedAnswers(currentState: WorkflowState, acceptedAnswers: string[]): boolean {
    return currentState.id === Constants.SUBMITTAL_APPROVAL_STATUS_ID && acceptedAnswers.length > 0;
  }

  static validateIfCanReturnWorkflow(doc: BaseDoc, currentState: WorkflowState, currentUserId: string, returnToState: WorkflowState | null | undefined): boolean {
    const isResponsible = currentState?.dueBy?.includes(currentUserId);
    const isCreator = doc.created_by === currentUserId;
    const validReturnToState = returnToState !== undefined;
    return (isResponsible || isCreator) && validReturnToState;
  }

  static validateNextStateHasAssignees(states: WorkflowState[], currentStateIndex: number): boolean {
    const nextState = states[currentStateIndex + 1];
    if (!nextState) {
      return true; // No next state means we're at the final state, can proceed
    }
    return nextState.dueBy && nextState.dueBy.length > 0;
  }
}
